
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Star, Zap } from 'lucide-react';

interface AccountUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentRole: string;
}

const AccountUpgradeModal: React.FC<AccountUpgradeModalProps> = ({ 
  isOpen, 
  onClose, 
  currentRole 
}) => {
  const [selectedPlan, setSelectedPlan] = useState<string>('');

  const upgradePlans = [
    {
      id: 'coach',
      title: 'Coach Account',
      price: '$9.99/month',
      icon: <Crown className="h-6 w-6" />,
      features: [
        'Team management tools',
        'Player statistics tracking',
        'Private team chats',
        'Training content upload',
        'Event scheduling'
      ],
      color: 'bg-blue-500'
    },
    {
      id: 'team-owner',
      title: 'Team Owner Account',
      price: '$19.99/month',
      icon: <Star className="h-6 w-6" />,
      features: [
        'Full team management',
        'Multiple team support',
        'Advanced analytics',
        'Venue booking access',
        'Sponsorship tools',
        'Revenue tracking'
      ],
      color: 'bg-purple-500'
    },
    {
      id: 'venue',
      title: 'Venue Account',
      price: '$14.99/month',
      icon: <Zap className="h-6 w-6" />,
      features: [
        'Venue listing management',
        'Booking calendar',
        'Revenue tracking',
        'Customer management',
        'Facility showcasing'
      ],
      color: 'bg-orange-500'
    }
  ];

  const visibilityUpgrades = [
    {
      id: 'profile-boost',
      title: 'Profile Boost',
      price: '$4.99',
      duration: '30 days',
      features: [
        'Featured in search results',
        'Profile highlighting',
        'Increased visibility'
      ]
    },
    {
      id: 'content-promotion',
      title: 'Content Promotion',
      price: '$9.99',
      duration: '7 days',
      features: [
        'Promoted posts',
        'Featured content',
        'Wider reach'
      ]
    }
  ];

  const handleUpgrade = (planId: string) => {
    // This would integrate with your payment system
    console.log('Upgrading to:', planId);
    // For now, just close the modal
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white text-2xl">Account Upgrades</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Account Type Upgrades */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Account Type Upgrades</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {upgradePlans.map((plan) => (
                <Card 
                  key={plan.id}
                  className={`bg-gray-800 border-gray-700 cursor-pointer transition-all ${
                    selectedPlan === plan.id ? 'ring-2 ring-sports-green' : ''
                  }`}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${plan.color}`}>
                        {plan.icon}
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg">{plan.title}</CardTitle>
                        <Badge className="bg-sports-green text-black">{plan.price}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-gray-300">
                          <Check className="h-4 w-4 text-sports-green" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handleUpgrade(plan.id)}
                      className="w-full mt-4 bg-sports-green text-black hover:bg-green-600"
                    >
                      Upgrade Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Visibility Upgrades */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Visibility & Promotion</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {visibilityUpgrades.map((upgrade) => (
                <Card key={upgrade.id} className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white">{upgrade.title}</CardTitle>
                    <div className="flex gap-2">
                      <Badge className="bg-sports-green text-black">{upgrade.price}</Badge>
                      <Badge variant="outline" className="border-gray-600 text-gray-300">
                        {upgrade.duration}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1 mb-4">
                      {upgrade.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-gray-300">
                          <Check className="h-4 w-4 text-sports-green" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handleUpgrade(upgrade.id)}
                      variant="outline" 
                      className="w-full border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountUpgradeModal;
